import { SetStateAction, useEffect, useMemo, useState } from 'react'

import debounce from 'debounce'
import { Select } from 'flowbite-react'
import { useParams } from 'react-router-dom'

import Loader from '@/components/Loader'
import TableItem from '@/components/Table'
import PaginationItem from '@/components/Table/pagination-item'
import SearchTableItem from '@/components/Table/search'
import { useDetalhePedidoContext } from '@/contexts/detalhe-pedido.context'
import { useGetRepasses } from '@/hooks/queries/use-get-pedidos'
import { TABLE_HEADERS_REPASSES_PEDIDO } from '@/pages/Pedidos/constants'
import { formatCurrency, roundMonetaryValue } from '@/utils'

const subtipoTaxa: { [status: string]: string } = {
  ['RepassePorPercentual']: 'Percentual',
  ['RepassePorColaborador']: 'Por Colaborador'
}

const DetailRepassesPedido = () => {
  const { pedido } = useDetalhePedidoContext()
  const { id } = useParams()
  const [search, setSearch] = useState('')
  const [repassesPage, setRepassesPage] = useState<number>(1)
  const [repassesPageSize, setRepassesPageSize] = useState(10)
  const repassesInicio = (repassesPage - 1) * repassesPageSize
  const {
    data: repassesList,
    isIdle,
    isLoading: isRepassesLoading
  } = useGetRepasses({
    params: {
      pedidoId: id,
      inicio: repassesInicio,
      comprimento: repassesPageSize,
      procurar: search,
      tipo: 'Repasse'
    }
  })

  const mappedRepasses = useMemo(() => {
    return repassesList?.conteudo?.resultados?.map((item: any) => {
      return {
        tipo: subtipoTaxa[item.subtipoTaxa],
        descricao: item.descricao,
        valorUnitario:
          item.subtipoTaxa === 'RepassePorPercentual'
            ? '-'
            : item.valor.toLocaleString('pt-br', {
                style: 'currency',
                currency: 'BRL'
              }),
        quantidade: item.quantidade,
        valorTotal: (() => {
          return roundMonetaryValue(item.valorTotal).toLocaleString('pt-br', {
            style: 'currency',
            currency: 'BRL'
          })
        })(),
        valorTotalRaw: item.valorTotal
      }
    })
  }, [repassesList])

  const repassesTotalPages = Math.ceil(
    (repassesList?.conteudo?.quantidadeDeItems ?? 0) / repassesPageSize
  )

  const handleSearch = debounce((e: SetStateAction<string>) => {
    setSearch(e)
  }, 500)

  const valorTotal = formatCurrency(pedido?.conteudo?.taxasOperadora ?? 0)

  useEffect(() => {
    repassesPage > 1 && setRepassesPage(1)
  }, [repassesPageSize])

  return (
    <div className='overflow-x-auto'>
      <SearchTableItem
        isAddItem={false}
        isSearchInputEnabled={true}
        setFilterText={e => handleSearch(e)}
      />
      {isRepassesLoading ? (
        <div
          className='h-96 w-full flex justify-center items-center'
          data-testid='loader-repasses'
        >
          <Loader size={'xl'} />
        </div>
      ) : (
        <TableItem
          isActions={false}
          isRowClicable={false}
          data={mappedRepasses || []}
          headers={TABLE_HEADERS_REPASSES_PEDIDO}
          deleteAction={() => void 0}
          hasCustomActions={false}
          idKey='pedidoId'
          isIdle={isIdle}
        />
      )}
      {(pedido?.conteudo?.taxasOperadora ?? 0) > 0 && (
        <div className='w-full pt-5 flex items-center justify-end'>
          <span className='font-bold text-xl' data-testid='total-value-text'>
            Valor total geral:{' '}
            <span className='text-quaternary'>{valorTotal}</span>
          </span>
        </div>
      )}
      <PaginationItem
        totalItems={repassesList?.conteudo?.quantidadeDeItems ?? 0}
        totalPages={repassesTotalPages}
        pageSize={repassesPageSize}
        onPageChange={(pageParam: number) => setRepassesPage(pageParam)}
        forceDisplay={true}
      >
        <Select
          id='select-page-size'
          data-testid='select-page-size'
          onChange={e => setRepassesPageSize(Number(e.target.value))}
          defaultValue={repassesPageSize}
        >
          <option value='10'>10</option>
          <option value='25'>25</option>
          <option value='50'>50</option>
          <option value='100'>100</option>
        </Select>
      </PaginationItem>
    </div>
  )
}

export default DetailRepassesPedido
